import React from 'react';
import htmr from 'htmr';
import { styled } from '@mui/material/styles';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import SEO from '../../../Components/common/SEO/SEO';
import styles from './jobs.module.scss';
import configData from '../../../config.json';
import SaveButton from '../../../Components/common/buttons/SaveButton/SaveButton';
import { saveCurrentJob, unsaveCurrentJob } from '../../../actions/job';
import { createLinkForSharingJob, removeStyleAttribute } from '../../../utils/helpers';
import ShareButton from '../../../Components/common/buttons/ShareButton/ShareButton';
import { useWindowSize } from '../../../utils/hooks';
import { MOBILE_BREAKPOINT } from '../../../utils/constants';
import colors from '../../../scss/colors';

const Container = styled('div')({
  display: 'flex',
  flex: '1 1 100%',
  flexDirection: 'column'
});

const JobTitleContainer = styled('div')((props) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'flex-start',
  marginBottom: '10px',
  rowGap: '8px',
  [props.theme.breakpoints.up('450')]: {
    flexWrap: 'nowrap',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    columnGap: '20px'
  }
}));

const JobTitle = styled('h4')({
  fontWeight: 350,
  fontSize: '20px',
  lineHeight: '24px',
  margin: 0
});

const TagsContainer = styled('div')({
  display: 'flex',
  rowGap: '8px',
  flexWrap: 'wrap'
});

const Tag = styled('h6', { shouldForwardProp: (prop) => prop !== 'isJobId' })(({ isJobId }) => ({
  backgroundColor: isJobId ? colors.LIGHT_BLUE : ` ${colors.LIGHTER_GREY}`,
  color: isJobId ? colors.PRIMARY_BLUE : colors.MEDIUM_GREY,
  fontSize: '12px',
  lineHeight: '14px',
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontWeight: 325,
  margin: 0,
  marginRight: '15px',
  padding: '2px 3px',
  width: '92px',
  borderRadius: '4px',
  svg: {
    fontSize: '12px',
    marginRight: 'auto'
  },
  span: {
    flex: '1',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center'
  }
}));

function JobCard(props) {
  const { job, applyHandler, saving = false } = props;

  const size = useWindowSize();

  return (
    <Container>
      <SEO
        title={job.JobTitle}
        description={job.JobDescription}
        name="airswift"
        type="website"
        imageUrl={`${configData.REACT_APP_PUBLIC_URL}/AirswiftPrimaryLogo.png`}
        contentUrl={`${configData.REACT_APP_PUBLIC_URL}/jobs/${job.Id}`}
      />
      <JobTitleContainer>
        <JobTitle>{job.JobTitle}</JobTitle>
        {job.IsApplied ? (
          <span className={styles.applied}>Applied</span>
        ) : (
          <span className={`${styles.job_apply}`} aria-hidden="true" onClick={() => applyHandler()}>
            Apply now
          </span>
        )}
        <SaveButton
          isSaved={job.IsSaved}
          isLarge
          id={job.Id}
          saveDispatcher={saveCurrentJob}
          unsaveDispatcher={unsaveCurrentJob}
          saving={saving}
        />
        {size.width > MOBILE_BREAKPOINT && (
          <ShareButton
            isLarge
            title={job?.JobTitle}
            text={job?.JobDescription}
            url={createLinkForSharingJob(window.location.origin, job.Id)}
          />
        )}
      </JobTitleContainer>
      <TagsContainer>
        <Tag isJobId>
          <span>{job.Id}</span>
        </Tag>
        <Tag>
          <LocationOnOutlined /> <span>{job.Location}</span>
        </Tag>
        <Tag>
          <DescriptionOutlinedIcon /> <span>{job.JobType}</span>
        </Tag>
        <Tag>
          <CalendarMonthOutlinedIcon />
          <span>{job.WorkSchedule || 'Full-Time'}</span>
        </Tag>
      </TagsContainer>
      <div className="pt-5">
        <p>{htmr(removeStyleAttribute(job.JobDescription))}</p>
      </div>
    </Container>
  );
}

export default JobCard;
